import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { AdminOrderDetailDto, AdminCreateOrderDto, AdminUpdateOrderDto, OrderStatus, PaymentStatus, AdminUserListDto } from '../../types';
import { ValidatedInput } from './ValidatedInput';
import { ValidationError } from './ValidationError';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import { parseAxiosValidationErrors } from '../../utils/productValidation';
import AdminModalShell from './AdminModalShell';

interface OrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedOrder: AdminOrderDetailDto | null;
    createFormData: AdminCreateOrderDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<AdminCreateOrderDto>>;
    updateFormData: AdminUpdateOrderDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<AdminUpdateOrderDto>>;
    handleSaveOrder: () => Promise<void>;
    users: AdminUserListDto[]; // New prop for dropdown
    isLoading: boolean; // General loading for core user data
    additionalFields: string[];
}
const OrderModal: React.FC<OrderModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedOrder,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    handleSaveOrder,
    users,
    isLoading,
    additionalFields,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    if (!isOpen) return null;
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        const formData = isCreating ? createFormData : updateFormData;
        if (isCreating && !formData.buyerId) newErrors.BuyerId = 'Buyer is required'; // Only for create
        const total = formData.total ?? 0;
        if (total < 0) newErrors.Total = 'Cannot be negative';
        const shippingCost = formData.shippingCost ?? 0;
        if (shippingCost < 0) newErrors.ShippingCost = 'Cannot be negative';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async () => {
        setErrors({});
        if (!validateForm()) {
            scrollToFirstErrorField();
            return;
        }
        try {
            await handleSaveOrder();
        } catch (err: unknown) {
            if (err instanceof AxiosError && err.response) {
                const backendErrors = parseAxiosValidationErrors(err);
                setErrors(backendErrors);
                scrollToFirstErrorField(backendErrors);
            } else if (err instanceof Error) {
                setErrors({ General: 'Save failed: ' + err.message });
            } else {
                setErrors({ General: 'Save failed: Unknown error' });
            }
        }
    };
    const formatDate = (isoString?: string): string => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    };
    const isSaveDisabled = isLoading || (!isCreating && selectedOrder === null);
    if (!isOpen) return null;
    return (
        <AdminModalShell
            title={isCreating ? 'Create Order' : 'Edit Order'}
            onClose={onClose}
            size="md"
        >
            <ValidationError error={errors.General} />
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buyer</label>
                    {isCreating ? (
                        <select
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                            value={createFormData.buyerId}
                            onChange={(e) => setCreateFormData({ ...createFormData, buyerId: e.target.value })}
                        >
                            <option value="">Select a buyer</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.email} ({user.userName})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm"
                            value={selectedOrder?.buyerId ?? ''}
                            disabled
                        />
                    )}
                    <ValidationError error={errors.BuyerId} />
                </div>
                <div>
                    <ValidatedInput
                        label="Product ID"
                        name="productId"
                        type="number"
                        clampMin={0}
                        step="1"
                        value={isCreating ? createFormData.productId : selectedOrder?.productId ?? 0}
                        onChange={(e) => {
                            if (isCreating) { // Only editable when creating
                                const value = parseInt(e.target.value, 10);
                                setCreateFormData({ ...createFormData, productId: value });
                            }
                        }}
                        disabled={!isCreating}
                        error={errors.ProductId}
                    />
                    <ValidationError error={errors.ProductId} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shopify Order ID</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shopifyOrderId')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shopifyOrderId : updateFormData.shopifyOrderId ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shopifyOrderId: e.target.value }) : setUpdateFormData({ ...updateFormData, shopifyOrderId: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shopifyOrderId')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shopifyOrderId') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShopifyOrderId} />
                </div>
                <div>
                    <ValidatedInput
                        label="Total to pay"
                        name="total"
                        type="number"
                        clampMin={0}
                        step="0.01"
                        value={isCreating ? createFormData.total : updateFormData.total ?? 0}
                        onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (isCreating) setCreateFormData({ ...createFormData, total: value });
                            else setUpdateFormData({ ...updateFormData, total: value });
                        }}
                        error={errors.Total}
                    />
                    <ValidationError error={errors.Total} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="relative">
                        <select
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('status')
                                    ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.status : updateFormData.status ?? 'Pending'}
                            onChange={(e) => {
                                const value = e.target.value as OrderStatus;
                                isCreating ? setCreateFormData({ ...createFormData, status: value }) : setUpdateFormData({ ...updateFormData, status: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('status')}
                        >
                            {Object.values(OrderStatus).map(stat => <option key={stat} value={stat}>{stat}</option>)}
                        </select>
                        {!isCreating && isLoading && additionalFields.includes('status') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.Status} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipped Date (ISO)</label>
                    <div className="relative">
                        <input
                            type="text"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippedDate')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? '' : updateFormData.shippedDate ?? ''}
                            onChange={(e) => !isCreating && setUpdateFormData({ ...updateFormData, shippedDate: e.target.value.trim() === '' ? undefined : e.target.value })}
                            disabled={isCreating || (!isCreating && isLoading && additionalFields.includes('shippedDate'))}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippedDate') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippedDate} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('paymentMethod')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.paymentMethod ?? '' : updateFormData.paymentMethod ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, paymentMethod: e.target.value }) : setUpdateFormData({ ...updateFormData, paymentMethod: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('paymentMethod')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('paymentMethod') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.PaymentMethod} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <div className="relative">
                        <select
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('paymentStatus')
                                    ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.paymentStatus : updateFormData.paymentStatus ?? 'Pending'}
                            onChange={(e) => {
                                const value = e.target.value as PaymentStatus;
                                isCreating ? setCreateFormData({ ...createFormData, paymentStatus: value }) : setUpdateFormData({ ...updateFormData, paymentStatus: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('paymentStatus')}
                        >
                            {Object.values(PaymentStatus).map(stat => <option key={stat} value={stat}>{stat}</option>)}
                        </select>
                        {!isCreating && isLoading && additionalFields.includes('paymentStatus') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.PaymentStatus} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" />
                    <div className="relative">
                        <ValidatedInput
                            label="Shipping Cost"  // Empty label since we have one above
                            name="shippingCost"  // Fixed typo from your original ('s hippingcost')
                            type="number"
                            clampMin={0}
                            step="0.01"
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingCost')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingCost : updateFormData.shippingCost ?? 0}
                            onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                isCreating ? setCreateFormData({ ...createFormData, shippingCost: value }) : setUpdateFormData({ ...updateFormData, shippingCost: value });
                            }}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingCost')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingCost') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingCost} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Method</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingMethod')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingMethod ?? '' : updateFormData.shippingMethod ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shippingMethod: e.target.value }) : setUpdateFormData({ ...updateFormData, shippingMethod: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingMethod')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingMethod') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingMethod} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('trackingNumber')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.trackingNumber ?? '' : updateFormData.trackingNumber ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, trackingNumber: e.target.value }) : setUpdateFormData({ ...updateFormData, trackingNumber: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('trackingNumber')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('trackingNumber') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.TrackingNumber} />
                </div>
                <div className="flex items-center">
                    <label className="block text-sm font-medium text-gray-700 mr-2">Is Disputed</label>
                    <input
                        type="checkbox"
                        checked={isCreating ? (createFormData.isDisputed ?? false) : (updateFormData.isDisputed ?? selectedOrder?.isDisputed ?? false)}
                        onChange={(e) => {
                            const value = e.target.checked;
                            isCreating ? setCreateFormData({ ...createFormData, isDisputed: value }) : setUpdateFormData({ ...updateFormData, isDisputed: value });
                        }}
                        disabled={!isCreating && isLoading && additionalFields.includes('isDisputed')}
                        className={`${!isCreating && isLoading && additionalFields.includes('isDisputed') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {!isCreating && isLoading && additionalFields.includes('isDisputed') && (
                        <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                    )}
                    <ValidationError error={errors.IsDisputed} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                    <div className="relative">
                        <textarea
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('adminNotes')
                                    ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.adminNotes ?? '' : updateFormData.adminNotes ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, adminNotes: e.target.value }) : setUpdateFormData({ ...updateFormData, adminNotes: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('adminNotes')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('adminNotes') && (
                            <div className="absolute bottom-2 right-2">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.AdminNotes} />
                </div>
                {/* Shipping Address */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Street Address</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingStreetAddress')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingStreetAddress ?? '' : updateFormData.shippingStreetAddress ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shippingStreetAddress: e.target.value }) : setUpdateFormData({ ...updateFormData, shippingStreetAddress: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingStreetAddress')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingStreetAddress') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingStreetAddress} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipping City</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingCity')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingCity ?? '' : updateFormData.shippingCity ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shippingCity: e.target.value }) : setUpdateFormData({ ...updateFormData, shippingCity: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingCity')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingCity') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingCity} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Postal Code</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingPostalCode')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingPostalCode ?? '' : updateFormData.shippingPostalCode ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shippingPostalCode: e.target.value }) : setUpdateFormData({ ...updateFormData, shippingPostalCode: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingPostalCode')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingPostalCode') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingPostalCode} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Country</label>
                    <div className="relative">
                        <input
                            className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${!isCreating && isLoading && additionalFields.includes('shippingCountry')
                                    ? 'pr-10 opacity-50 cursor-not-allowed bg-gray-100'
                                    : ''
                                }`}
                            value={isCreating ? createFormData.shippingCountry ?? '' : updateFormData.shippingCountry ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, shippingCountry: e.target.value }) : setUpdateFormData({ ...updateFormData, shippingCountry: e.target.value })}
                            disabled={!isCreating && isLoading && additionalFields.includes('shippingCountry')}
                        />
                        {!isCreating && isLoading && additionalFields.includes('shippingCountry') && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            </div>
                        )}
                    </div>
                    <ValidationError error={errors.ShippingCountry} />
                </div>
            </div>
            {!isCreating && selectedOrder && (
                <>
                    <h3 className="mt-6 text-lg font-bold text-gray-800">Additional Details</h3>
                    {!isLoading ? (
                        <>
                            <p>Order Date: {formatDate(selectedOrder.orderDate)}</p>
                            <p>Shipped Date: {formatDate(selectedOrder.shippedDate)}</p>
                            <p>Is Disputed: {selectedOrder.isDisputed ? 'Yes' : 'No'}</p>
                        </>
                    ) : (
                        <div className="flex items-center justify-center py-4">
                            <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-600">Loading additional details...</p>
                        </div>
                    )}
                </>
            )}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3"
                    disabled={isSaveDisabled}>Save</button>
                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
            </div>
        </AdminModalShell>
    );
};
export default OrderModal;